// src/lib/featureFlags.ts
//
// Feature flags — fail-closed gating for admin surfaces that still run
// on seeded demo data or localStorage stores instead of the live API
// (M2-00 work package, acceptance-matrix row A21).
//
// Contract:
//   - Every flag defaults to DISABLED. A surface is available only
//     when its VITE_FEATURE_* env var is explicitly set to "1" or
//     "true" at build time.
//   - Unknown flag names, undefined env vars and any other value
//     ("0", "yes", "", typos) all mean DISABLED. The mechanism fails
//     closed by construction.
//   - A disabled route renders a clear "not available in this build"
//     notice and must NEVER render demo/localStorage rows as if they
//     were real business records.
//
// Flags are read at call time (not cached at module init) so tests can
// stub import.meta.env per case via vi.stubEnv().

/** Map from typed flag name to the build-time env var that controls it. */
export const FEATURE_FLAG_ENV = {
  adminApprovals: "VITE_FEATURE_ADMIN_APPROVALS",
  adminCampaigns: "VITE_FEATURE_ADMIN_CAMPAIGNS",
  adminProducts: "VITE_FEATURE_ADMIN_PRODUCTS",
  adminUsers: "VITE_FEATURE_ADMIN_USERS",
  adminFilemakerSync: "VITE_FEATURE_ADMIN_FILEMAKER_SYNC",
  adminFmSyncClient: "VITE_FEATURE_ADMIN_FM_SYNC_CLIENT",
} as const;

export type FeatureFlag = keyof typeof FEATURE_FLAG_ENV;

/** Human-readable surface names for notices and documentation. */
export const FEATURE_FLAG_LABELS: Record<FeatureFlag, string> = {
  adminApprovals: "Approvals queue",
  adminCampaigns: "Marketing campaigns",
  adminProducts: "Products admin",
  adminUsers: "Users & roles",
  adminFilemakerSync: "FileMaker sync monitor",
  adminFmSyncClient: "FileMaker sync clients",
};

/** Why each surface is gated (shown in the disabled notice). */
export const FEATURE_FLAG_REASONS: Record<FeatureFlag, string> = {
  adminApprovals:
    "runs on seeded demo approval items; no approvals backend exists yet",
  adminCampaigns:
    "runs on a browser-local demo store; no campaigns backend exists yet",
  adminProducts:
    "edits seeded demo products in memory; not wired to the live catalog API",
  adminUsers:
    "renders a static seed mirror of the Keycloak realm, not live data",
  adminFilemakerSync:
    "renders deterministic demo sync state until the real mirror feed ships",
  adminFmSyncClient:
    "reads the Rust agent's live client registry, which is not rolled out to every environment yet",
};

/**
 * Fail-closed flag check. Returns true only when the env var is
 * explicitly "1" or "true"; every other value (including undefined)
 * is disabled.
 */
export function isFeatureEnabled(flag: FeatureFlag): boolean {
  const raw = import.meta.env?.[FEATURE_FLAG_ENV[flag]];
  return raw === "1" || raw === "true";
}
